import React, { useEffect, useRef, useState } from 'react';

interface ImportZipModalProps {
  file: File;
  token: string;
  onClose: () => void;
  onSuccess: (inspectionId: string, incomplete: boolean) => void;
}

type StageStatus = 'pending' | 'active' | 'done' | 'flagged';

const STAGES = [
  { key: 'uploading', label: 'Uploading', icon: 'cloud_upload' },
  { key: 'extracting', label: 'Extracting', icon: 'folder_zip' },
  { key: 'validating', label: 'Validating', icon: 'verified' },
  { key: 'checking', label: 'Checking records', icon: 'manage_search' },
  { key: 'importing', label: 'Importing data', icon: 'sync' },
  { key: 'checks', label: 'Running checks', icon: 'fact_check' },
  { key: 'done', label: 'Done', icon: 'check_circle' },
] as const;

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_CHUNK_RETRIES = 3;

const baseUrl = () => import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const ImportZipModal: React.FC<ImportZipModalProps> = ({ file, token, onClose, onSuccess }) => {
  const [activeStage, setActiveStage] = useState(0);
  const [doneStages, setDoneStages] = useState<Set<number>>(new Set());
  const [flaggedNote, setFlaggedNote] = useState<string | null>(null);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [confirmResurrect, setConfirmResurrect] = useState(false);
  const [result, setResult] = useState<{ inspectionId: string; incomplete: boolean } | null>(null);

  const uploadIdRef = useRef<string | null>(null);
  const totalChunksRef = useRef(0);
  const simIntervalRef = useRef<number | null>(null);
  const startedRef = useRef(false);

  const totalMb = (file.size / (1024 * 1024)).toFixed(1);
  const uploadedMb = (uploadedBytes / (1024 * 1024)).toFixed(1);

  const authHeaders = (extra: Record<string, string> = {}) => ({
    Authorization: `Bearer ${token}`,
    ...extra,
  });

  const stopSim = () => {
    if (simIntervalRef.current) {
      window.clearInterval(simIntervalRef.current);
      simIntervalRef.current = null;
    }
  };

  // Once the real chunk upload finishes, the client has no more visibility
  // into what the server is doing until /finish resolves — so stages 2–6
  // advance on a timer purely as an honest "still working" indicator, and
  // freeze on "Running checks" until the real response lands.
  const startProcessingAnimation = () => {
    setActiveStage(1);
    let stage = 1;
    simIntervalRef.current = window.setInterval(() => {
      setDoneStages((prev) => new Set(prev).add(stage));
      stage += 1;
      if (stage >= 5) {
        setActiveStage(5);
        stopSim();
        return;
      }
      setActiveStage(stage);
    }, 900);
  };

  const runImport = async (resurrect = false) => {
    setError(null);
    setConfirmResurrect(false);
    try {
      if (!uploadIdRef.current) {
        setActiveStage(0);
        const startRes = await fetch(`${baseUrl()}/api/v1/final-inspections/import-zip/start`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ totalChunks: Math.ceil(file.size / CHUNK_SIZE) }),
        });
        const startData = await startRes.json();
        if (!startRes.ok || !startData.success) {
          throw new Error(startData.message || 'Could not start the import.');
        }
        uploadIdRef.current = startData.uploadId;
        totalChunksRef.current = Math.ceil(file.size / CHUNK_SIZE);

        for (let i = 0; i < totalChunksRef.current; i++) {
          const start = i * CHUNK_SIZE;
          const chunk = file.slice(start, start + CHUNK_SIZE);
          let attempt = 0;
          // Retry this one chunk a few times before giving up entirely —
          // never restart the whole upload over a single dropped piece.
          for (;;) {
            try {
              const formData = new FormData();
              formData.append('chunk', chunk, file.name);
              const chunkRes = await fetch(
                `${baseUrl()}/api/v1/final-inspections/import-zip/${uploadIdRef.current}/chunk/${i}`,
                { method: 'POST', headers: authHeaders(), body: formData }
              );
              if (!chunkRes.ok) throw new Error('Chunk upload failed');
              break;
            } catch (chunkErr) {
              attempt += 1;
              if (attempt >= MAX_CHUNK_RETRIES) {
                throw new Error(`Upload failed at ${Math.round((start / file.size) * 100)}% — please try again.`);
              }
            }
          }
          setUploadedBytes(Math.min(start + CHUNK_SIZE, file.size));
        }

        setDoneStages((prev) => new Set(prev).add(0));
        startProcessingAnimation();
      }

      const finishRes = await fetch(
        `${baseUrl()}/api/v1/final-inspections/import-zip/${uploadIdRef.current}/finish`,
        {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ confirmResurrect: resurrect }),
        }
      );
      const finishData = await finishRes.json();
      stopSim();

      if (finishRes.status === 409 && finishData.requiresConfirmation) {
        setConfirmResurrect(true);
        return;
      }
      if (!finishRes.ok || !finishData.success) {
        throw new Error(finishData.message || 'Import failed.');
      }

      setDoneStages((prev) => {
        const next = new Set(prev);
        for (let i = 0; i <= 5; i++) next.add(i);
        return next;
      });
      setActiveStage(6);
      if (finishData.recoveryIncomplete) {
        setFlaggedNote('Some required photos or signatures were missing.');
      }
      setResult({ inspectionId: finishData.inspectionId, incomplete: !!finishData.recoveryIncomplete });
    } catch (err: any) {
      stopSim();
      setError(err.message || 'Import failed. Please try again.');
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    runImport();
    return () => stopSim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = () => {
    uploadIdRef.current = null;
    setUploadedBytes(0);
    setDoneStages(new Set());
    setFlaggedNote(null);
    startedRef.current = true;
    runImport();
  };

  const stageStatus = (index: number): StageStatus => {
    if (doneStages.has(index)) return index === 5 && flaggedNote ? 'flagged' : 'done';
    if (activeStage === index) return 'active';
    return 'pending';
  };

  const iconClasses = (status: StageStatus) => {
    switch (status) {
      case 'active': return 'bg-blue-50 text-[#136bce] ring-4 ring-blue-100 animate-pulse';
      case 'done': return 'bg-emerald-50 text-emerald-600';
      case 'flagged': return 'bg-amber-50 text-amber-600';
      default: return 'bg-slate-100 text-slate-400';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#136bce] shrink-0">
            <span className="material-symbols-outlined text-[22px]">folder_zip</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Importing recovery file</p>
            <p className="truncate text-sm font-semibold text-slate-900">{file.name}</p>
          </div>
          {(result || error) && (
            <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          )}
        </div>

        {/* Stages */}
        <ol className="px-6 pt-5 pb-2">
          {STAGES.map((stage, index) => {
            const status = stageStatus(index);
            const isLast = index === STAGES.length - 1;
            return (
              <li key={stage.key} className="relative grid grid-cols-[36px_1fr] gap-3.5 pb-6 last:pb-1">
                {!isLast && (
                  <div className="absolute left-[17px] top-9 bottom-[-4px] w-0.5 bg-slate-100">
                    <div
                      className="w-full bg-emerald-400 transition-all duration-500"
                      style={{ height: status === 'done' || status === 'flagged' ? '100%' : '0%' }}
                    />
                  </div>
                )}
                <div className={`relative z-10 flex h-9 w-9 items-center justify-center rounded-full transition-colors ${iconClasses(status)}`}>
                  <span className="material-symbols-outlined text-[17px]">
                    {status === 'flagged' && index === 5 ? 'warning' : stage.icon}
                  </span>
                </div>
                <div className="pt-0.5">
                  <p className={`text-sm font-semibold ${status === 'pending' ? 'text-slate-400' : 'text-slate-900'}`}>{stage.label}</p>
                  {index === 0 && (status === 'active' || status === 'done') && (
                    <>
                      <p className="text-xs text-[#136bce] font-medium mt-0.5">
                        {uploadedMb} of {totalMb} MB
                      </p>
                      <div className="mt-1.5 h-1.5 w-48 max-w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-[#136bce] to-[#0e52a0] transition-all"
                          style={{ width: `${Math.min(100, (uploadedBytes / file.size) * 100)}%` }}
                        />
                      </div>
                    </>
                  )}
                  {index === 5 && flaggedNote && <p className="text-xs font-semibold text-amber-600 mt-0.5">{flaggedNote}</p>}
                  {index === 6 && result && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Report added &mdash; tagged{' '}
                      <strong className={result.incomplete ? 'text-amber-600' : 'text-emerald-600'}>
                        {result.incomplete ? 'Recovered · Incomplete' : 'Recovered'}
                      </strong>
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        {/* Confirmation: previously deleted */}
        {confirmResurrect && (
          <div className="mx-6 mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-800 mb-3">This inspection was previously deleted. Import anyway?</p>
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">
                CANCEL
              </button>
              <button onClick={() => runImport(true)} className="flex-1 rounded-lg bg-amber-600 px-3 py-2 text-xs font-bold text-white hover:bg-amber-700">
                IMPORT ANYWAY
              </button>
            </div>
          </div>
        )}

        {/* Terminal error */}
        {error && (
          <div className="mx-6 mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-700 mb-3">{error}</p>
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">
                CANCEL
              </button>
              <button onClick={retry} className="flex-1 rounded-lg bg-[#136bce] px-3 py-2 text-xs font-bold text-white hover:bg-blue-700">
                TRY AGAIN
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-3 border-t border-slate-100 px-6 py-4">
          <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500">
            <span className={`h-1.5 w-1.5 rounded-full ${result ? (result.incomplete ? 'bg-amber-500' : 'bg-emerald-500') : error ? 'bg-red-500' : 'bg-[#136bce] animate-pulse'}`} />
            {result ? (result.incomplete ? 'Recovered · Incomplete' : 'Recovered') : error ? 'Failed' : confirmResurrect ? 'Waiting for confirmation' : STAGES[activeStage]?.label + '…'}
          </span>
          <div className="flex-1" />
          {result && (
            <button
              onClick={() => onSuccess(result.inspectionId, result.incomplete)}
              className="rounded-lg bg-[#136bce] px-5 py-2 text-xs font-bold text-white hover:bg-blue-700 active:scale-95 transition-transform"
            >
              VIEW REPORT
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportZipModal;
